import { Platform, Text, View } from 'react-native';
import React, { Component, ErrorInfo, ReactNode } from 'react';

import { crashlyticsUtil } from '@lib/crashlytics';
import { trackEvent } from '@utils/analytics';

interface Props {
  children: ReactNode; // Explicitly define children here
  orderUUID: string;
}

interface State {
  hasError: boolean;
}

export class OrderErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 1. Log traditional GA4/Web counter analytics event (Runs on both Web and Android)
    trackEvent('order_detail_render_crash', {
      order_id: this.props.orderUUID,
      error_message: error.message,
      component_stack: errorInfo.componentStack?.substring(0, 1000),
    });

    if (Platform.OS !== 'web') {
      crashlyticsUtil.setAttribute('failed_order_uuid', this.props.orderUUID);
      crashlyticsUtil.setAttribute('error_boundary_location', 'OrderErrorBoundary');

      crashlyticsUtil.log(`OrderErrorBoundary caught a rendering crash for order: ${this.props.orderUUID}`);
      crashlyticsUtil.recordError(error);
    }
  }

  componentDidUpdate(prevProps: Props) {
    // If the user selects a different order, reset the error state
    // to try rendering the new order details.
    if (prevProps.orderUUID !== this.props.orderUUID) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <Text style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 8, color: '#FF3B30' }}>
            Something went wrong displaying this order.
          </Text>
          <Text style={{ textAlign: 'center', color: '#666' }}>
            ID: {this.props.orderUUID}
          </Text>
          <Text style={{ textAlign: 'center', color: '#8E8E93', marginTop: 4, fontSize: 12 }}>
            Please try selecting another order or refreshing.
          </Text>
        </View>
      );
    }

    return this.props.children;
  }
}
